/**
 * src/sections/rsvp.js — RSVP section ESM module (S0.8)
 *
 * Public-facing RSVP form. Phone-first lookup flow (S2.5 / lookupRsvpByPhone).
 * No window.* dependencies.
 */

import { storeGet, storeSet } from "../core/store.js";
import { BaseSection, fromSection } from "../core/section-base.js";
import { t } from "../core/i18n.js";
import { cleanPhone, isValidPhone } from "../utils/phone.js";
import { sanitize } from "../utils/sanitize.js";
import { nowISOJerusalem } from "../utils/date.js";
import { enqueueWrite, appendToRsvpLog, syncStoreKeyToSheets } from "../core/sync.js";
import { GUEST_SIDES, RSVP_RESPONSE_STATUSES, MEAL_TYPES } from "../core/constants.js";
import { vibrate, HAPTIC } from "../utils/haptic.js";
import { buildGoogleCalendarLink, buildIcsDataUrl } from "../utils/calendar-link.js";
import { buildWazeUrl, buildGoogleMapsUrl, buildOsmEmbedUrl } from "../utils/venue-links.js";
import {
  visibleQuestions as _visibleQuestions,
  validateAnswers as _validateAnswers,
  expandPlusOnes as _expandPlusOnes,
} from "../utils/rsvp-question-engine.js";
import {
  planReminders as _planReminders,
  nextDueWave as _nextDueWave,
} from "../utils/rsvp-reminder.js";

/** @type {HTMLElement|null} */
let _container = null;

class RsvpSection extends BaseSection {
  async onMount(/** @type {Record<string, unknown>} */ params) {
    _container = (params instanceof HTMLElement) ? params : null;
    // S12.5 — Check RSVP deadline
    if (_isRsvpDeadlinePassed()) {
      _showDeadlineMessage();
      return;
    }
    // S11.1 — Auto-lookup from URL guestId param
    _autoLookupFromUrl();
  }

  onUnmount() {
    _container = null;
  }
}

export const { mount, unmount, capabilities } = fromSection(new RsvpSection("rsvp"));

// ── S11.1 Auto-lookup from URL ────────────────────────────────────────────

/**
 * If URL contains ?guestId=xxx or ?phone=05x, auto-lookup and pre-fill.
 */
function _autoLookupFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const guestId = params.get("guestId");
  const phone = params.get("phone");

  if (guestId) {
    const guests = /** @type {any[]} */ (storeGet("guests") ?? []);
    const guest = guests.find((g) => g.id === guestId);
    if (guest) {
      _prefillForm(guest);
      const statusEl = document.getElementById("rsvpLookupStatus");
      if (statusEl) {
        statusEl.classList.remove("u-hidden");
        statusEl.textContent = t("rsvp_welcome_name").replace(
          "{name}",
          `${guest.firstName} ${guest.lastName || ""}`.trim(),
        );
      }
      return;
    }
  }
  if (phone) {
    const result = lookupRsvpByPhone(phone);
    const phoneInput = /** @type {HTMLInputElement|null} */ (document.getElementById("rsvpPhone"));
    if (phoneInput) phoneInput.value = phone;
    if (result.found) {
      const statusEl = document.getElementById("rsvpLookupStatus");
      if (statusEl) {
        statusEl.classList.remove("u-hidden");
        statusEl.textContent = t("rsvp_lookup_found");
      }
    }
  }
}

// ── RSVP flow ─────────────────────────────────────────────────────────────

/**
 * Phone-first lookup: search for guest by normalised phone, pre-fill form.
 * @param {string} rawPhone
 * @returns {{ found: boolean, guest?: any }}
 */
export function lookupRsvpByPhone(rawPhone) {
  if (!isValidPhone(rawPhone)) return { found: false };
  const phone = cleanPhone(rawPhone);
  const guests = /** @type {any[]} */ (storeGet("guests") ?? []);
  const guest = guests.find((g) => cleanPhone(g.phone || "") === phone);
  if (guest) {
    _prefillForm(guest);
    return { found: true, guest };
  }
  return { found: false };
}

/**
 * Submit the RSVP form.
 * @param {Record<string, unknown>} data
 * @returns {{ ok: boolean, errors?: string[] }}
 */
export function submitRsvp(data) {
  const { value, errors } = sanitize(data, {
    phone: { type: "string", required: true },
    firstName: { type: "string", required: false, maxLength: 80 },
    lastName: { type: "string", required: false, maxLength: 80 },
    side: { type: "enum", values: [...GUEST_SIDES], default: "mutual" },
    status: {
      type: "enum",
      values: [...RSVP_RESPONSE_STATUSES],
      required: true,
    },
    count: { type: "number", min: 1, max: 50, default: 1 },
    children: { type: "number", min: 0, max: 20, default: 0 },
    meal: {
      type: "enum",
      values: [...MEAL_TYPES, "other"],
      default: "regular",
    },
    accessibility: { type: "string", required: false, maxLength: 10 },
    transport: { type: "string", required: false, maxLength: 60 },
    notes: { type: "string", required: false, maxLength: 500 },
  });
  if (errors.length) return { ok: false, errors };

  const phone = cleanPhone(String(value.phone));
  const guests = [.../** @type {any[]} */ (storeGet("guests") ?? [])];
  const idx = guests.findIndex((g) => cleanPhone(g.phone || "") === phone);

  const now = nowISOJerusalem();
  if (idx !== -1) {
    guests[idx] = {
      ...guests[idx],
      status: value.status,
      count: value.count,
      children: value.children,
      meal: value.meal,
      notes: value.notes ?? guests[idx].notes,
      ...(value.firstName ? { firstName: value.firstName } : {}),
      ...(value.lastName !== undefined ? { lastName: value.lastName } : {}),
      ...(value.side ? { side: value.side } : {}),
      accessibility: value.accessibility === "true",
      transport: value.transport ?? guests[idx].transport,
      rsvpDate: now,
      updatedAt: now,
    };
  } else {
    // Unknown phone — create a new pending guest record
    guests.push({
      id: `rsvp-${phone}`,
      phone,
      firstName: /** @type {string} */ (value.firstName) || "",
      lastName: /** @type {string} */ (value.lastName) || "",
      side: value.side || "mutual",
      status: value.status,
      count: value.count,
      children: value.children,
      meal: value.meal,
      accessibility: value.accessibility === "true",
      transport: value.transport || "",
      notes: value.notes ?? "",
      rsvpDate: now,
      createdAt: now,
      updatedAt: now,
    });
  }

  storeSet("guests", guests);
  enqueueWrite("guests", () => syncStoreKeyToSheets("guests"));
  enqueueWrite("rsvp_log", () =>
    appendToRsvpLog({
      phone,
      firstName: String(value.firstName ?? ""),
      lastName: String(value.lastName ?? ""),
      status: String(value.status),
      count: Number(value.count ?? 1),
      timestamp: now,
    }),
  );

  // Register Background Sync so offline submissions flush automatically once online (S18b)
  if (!navigator.onLine && "serviceWorker" in navigator && "SyncManager" in window) {
    navigator.serviceWorker.ready
      .then((reg) => /** @type {any} */ (reg).sync.register("rsvp-sync"))
      .catch(() => {
        /* Background Sync not available */
      });
  }

  vibrate(HAPTIC.SUCCESS); // S23 haptic feedback on RSVP submit
  _showConfirmation(/** @type {string} */ (value.status));
  return { ok: true };
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Pre-fill RSVP form fields from an existing guest record. */
function _prefillForm(/** @type {any} */ guest) {
  /** @type {Record<string,string>} */
  const fieldMap = {
    phone: "rsvpPhone",
    firstName: "rsvpFirstName",
    lastName: "rsvpLastName",
    count: "rsvpGuests",
    children: "rsvpChildren",
    meal: "rsvpMeal",
    notes: "rsvpNotes",
    side: "rsvpSide",
    transport: "rsvpTransport",
  };
  Object.entries(fieldMap).forEach(([key, id]) => {
    const input = /** @type {HTMLInputElement|HTMLSelectElement|null} */ (
      document.getElementById(id)
    );
    if (input && guest[key] !== undefined) input.value = String(guest[key]);
  });
  // Accessibility checkbox
  const acc = /** @type {HTMLInputElement|null} */ (document.getElementById("rsvpAccessibility"));
  if (acc) acc.checked = Boolean(guest.accessibility);
  // Reveal the rest of the form
  const formBody = document.getElementById("rsvpDetails");
  if (formBody) formBody.classList.remove("u-hidden");
}

/** Show RSVP success message. */
function _showConfirmation(/** @type {string} */ status) {
  const confirmEl = document.getElementById("rsvpConfirm");
  if (!confirmEl) return;
  if (status === "confirmed") {
    confirmEl.textContent = t("rsvp_confirmed");
    _renderCalendarLinks(confirmEl);
    _renderVenueLinks(confirmEl);
    _renderVirtualLink(confirmEl); // S431: show join link for hybrid events
  } else if (status === "declined") {
    confirmEl.textContent = t("rsvp_declined");
  } else {
    confirmEl.textContent = t("saved");
  }
  confirmEl.classList.remove("u-hidden");
  const details = document.getElementById("rsvpDetails");
  if (details) details.classList.add("u-hidden");
}

/**
 * Append "Add to Calendar" actions (Google Calendar link + .ics download)
 * to the confirmation panel. Skips silently if weddingInfo lacks a date.
 * No `innerHTML` — DOM built via `createElement` + `textContent`.
 *
 * @param {HTMLElement} parent
 */
function _renderCalendarLinks(parent) {
  const info = /** @type {Record<string, string|undefined>} */ (storeGet("weddingInfo") ?? {});
  const date = info.date;
  if (!date) return;
  const time = info.time || "19:00";
  const start = new Date(`${date}T${time}:00`);
  if (Number.isNaN(start.getTime())) return;
  const title = `${info.groom || ""} & ${info.bride || ""}`.trim() || t("rsvp_add_to_calendar");
  /** @type {import("../utils/calendar-link.js").CalendarEvent} */
  const ev = {
    title,
    location: info.venue || "",
    start,
  };

  const wrap = document.createElement("div");
  wrap.className = "rsvp-calendar-actions";
  wrap.style.marginTop = "0.75rem";
  wrap.style.display = "flex";
  wrap.style.gap = "0.5rem";
  wrap.style.flexWrap = "wrap";

  const heading = document.createElement("strong");
  heading.textContent = t("rsvp_add_to_calendar");
  heading.style.flex = "1 0 100%";
  wrap.appendChild(heading);

  const gcal = document.createElement("a");
  gcal.href = buildGoogleCalendarLink(ev);
  gcal.target = "_blank";
  gcal.rel = "noopener noreferrer";
  gcal.className = "btn btn-sm";
  gcal.textContent = t("rsvp_add_to_google");
  wrap.appendChild(gcal);

  const ics = document.createElement("a");
  ics.href = buildIcsDataUrl(ev);
  ics.download = "wedding.ics";
  ics.className = "btn btn-sm";
  ics.textContent = t("rsvp_download_ics");
  wrap.appendChild(ics);

  parent.appendChild(wrap);
}

/**
 * S431: Append a "Join Online" button if the couple has set a virtual/hybrid link.
 * Safe: only renders if virtualLink is an https URL.
 * @param {HTMLElement} parent
 */
function _renderVirtualLink(parent) {
  const info = /** @type {Record<string, string|undefined>} */ (storeGet("weddingInfo") ?? {});
  const url = info.virtualLink ?? "";
  if (!url.startsWith("https://")) return;

  const wrap = document.createElement("div");
  wrap.className = "rsvp-virtual-actions";
  wrap.style.marginTop = "0.75rem";

  const label = document.createElement("strong");
  label.textContent = t("rsvp_virtual_label");
  label.style.display = "block";
  label.style.marginBottom = "0.5rem";
  wrap.appendChild(label);

  const btn = document.createElement("a");
  btn.href = url;
  btn.target = "_blank";
  btn.rel = "noopener noreferrer";
  btn.className = "btn btn-primary";
  btn.textContent = t("rsvp_join_online");
  wrap.appendChild(btn);

  parent.appendChild(wrap);
}

/**
 * S421: Append venue direction links (Waze, Google Maps) and an OSM embed
 * to the confirmation panel. Skips silently if weddingInfo lacks lat/lon.
 * @param {HTMLElement} parent
 */
function _renderVenueLinks(parent) {
  const info = /** @type {Record<string, any>} */ (storeGet("weddingInfo") ?? {});
  const lat = parseFloat(info.lat);
  const lon = parseFloat(info.lon);
  if (!lat || !lon || Number.isNaN(lat) || Number.isNaN(lon)) return;

  const wrap = document.createElement("div");
  wrap.className = "rsvp-venue-actions";
  wrap.style.marginTop = "0.75rem";
  wrap.style.display = "flex";
  wrap.style.gap = "0.5rem";
  wrap.style.flexWrap = "wrap";

  const heading = document.createElement("strong");
  heading.textContent = t("venue_directions_title");
  heading.style.flex = "1 0 100%";
  wrap.appendChild(heading);

  const waze = document.createElement("a");
  waze.href = buildWazeUrl(lat, lon);
  waze.target = "_blank";
  waze.rel = "noopener noreferrer";
  waze.className = "btn btn-sm";
  waze.textContent = t("venue_directions_waze");
  wrap.appendChild(waze);

  const gmaps = document.createElement("a");
  gmaps.href = buildGoogleMapsUrl(lat, lon);
  gmaps.target = "_blank";
  gmaps.rel = "noopener noreferrer";
  gmaps.className = "btn btn-sm";
  gmaps.textContent = t("venue_directions_google");
  wrap.appendChild(gmaps);

  // OSM embed (iframe)
  const osmUrl = buildOsmEmbedUrl(lat, lon);
  const iframe = document.createElement("iframe");
  iframe.src = osmUrl;
  iframe.width = "100%";
  iframe.height = "200";
  iframe.style.flex = "1 0 100%";
  iframe.style.border = "none";
  iframe.style.borderRadius = "8px";
  iframe.style.marginTop = "0.5rem";
  iframe.title = t("venue_map_embed");
  iframe.loading = "lazy";
  wrap.appendChild(iframe);

  parent.appendChild(wrap);
}

// ── S12.5 RSVP Deadline Enforcement ───────────────────────────────────────

/**
 * Check if the RSVP deadline has passed.
 * @returns {boolean}
 */
function _isRsvpDeadlinePassed() {
  const info = /** @type {Record<string, string|undefined>} */ (storeGet("weddingInfo") ?? {});
  const deadline = info.rsvpDeadline;
  if (!deadline) return false;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  return now > deadlineDate;
}

/**
 * Show a "RSVP is closed" message and disable the form.
 */
function _showDeadlineMessage() {
  if (!_container) return;
  const form = _container.querySelector(".rsvp-form");
  if (form) {
    form.textContent = "";
    const msg = document.createElement("div");
    msg.className = "rsvp-deadline-msg u-text-center u-p-lg";
    const icon = document.createElement("div");
    icon.textContent = "⏰";
    icon.style.fontSize = "3rem";
    msg.appendChild(icon);
    const p = document.createElement("p");
    p.textContent =
      t("rsvp_deadline_passed") || "RSVP deadline has passed. Please contact the couple directly.";
    p.style.fontSize = "1.2rem";
    p.style.marginTop = "1rem";
    msg.appendChild(p);
    form.appendChild(msg);
  }
}

/**
 * Response rate by side.
 * @returns {{ side: string, total: number, responded: number, rate: number }[]}
 */
export function getRsvpRateBySide() {
  const guests = /** @type {any[]} */ (storeGet("guests") ?? []);
  /** @type {Map<string, { total: number, responded: number }>} */
  const map = new Map();
  for (const guest of guests) {
    const side = guest.side || "mutual";
    const entry = map.get(side) ?? { total: 0, responded: 0 };
    entry.total += 1;
    if (RSVP_RESPONSE_STATUSES.includes(guest.status)) {
      entry.responded += 1;
    }
    map.set(side, entry);
  }
  return [...map.entries()].map(([side, values]) => ({
    side,
    total: values.total,
    responded: values.responded,
    rate: values.total ? Math.round((values.responded / values.total) * 100) : 0,
  }));
}

/**
 * Average response time in days.
 * @returns {{ avgDays: number, fastest: number, slowest: number, count: number }}
 */
export function getRsvpResponseTime() {
  const guests = /** @type {any[]} */ (storeGet("guests") ?? []);
  const times = guests
    .filter((guest) => guest.createdAt && guest.rsvpDate)
    .map(
      (guest) =>
        (new Date(guest.rsvpDate).getTime() - new Date(guest.createdAt).getTime()) / 86400000,
    )
    .filter((days) => days >= 0);
  if (times.length === 0) {
    return { avgDays: 0, fastest: 0, slowest: 0, count: 0 };
  }
  const total = times.reduce((sum, days) => sum + days, 0);
  return {
    avgDays: Math.round(total / times.length),
    fastest: Math.round(Math.min(...times)),
    slowest: Math.round(Math.max(...times)),
    count: times.length,
  };
}

/**
 * RSVP submissions per day.
 * @returns {{ date: string, count: number }[]}
 */
export function getRsvpDailyTrend() {
  const guests = /** @type {any[]} */ (storeGet("guests") ?? []);
  /** @type {Map<string, number>} */
  const byDay = new Map();
  for (const guest of guests) {
    if (!guest.rsvpDate) continue;
    const day = String(guest.rsvpDate).slice(0, 10);
    byDay.set(day, (byDay.get(day) ?? 0) + 1);
  }
  return [...byDay.entries()]
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// ── S607: Conditional RSVP question engine integration ─────────────────────

/**
 * Read the custom RSVP questions configured on `weddingInfo.rsvpQuestions`.
 * Each question follows the {@link import("../utils/rsvp-question-engine.js").Question} shape.
 * @returns {any[]}
 */
export function getRsvpQuestions() {
  const info = /** @type {any} */ (storeGet("weddingInfo") ?? {});
  return Array.isArray(info.rsvpQuestions) ? info.rsvpQuestions : [];
}

/**
 * Compute which custom questions should be visible for the supplied
 * answers map (after evaluating each question's `showWhen` condition).
 * @param {Record<string, unknown>} answers
 * @returns {any[]}
 */
export function getVisibleRsvpQuestions(answers) {
  return _visibleQuestions(getRsvpQuestions(), answers ?? {});
}

/**
 * Validate the supplied answers map against currently-visible required
 * questions. Returns `{ valid, missing }` — `missing` contains the IDs
 * of unanswered required questions.
 * @param {Record<string, unknown>} answers
 * @returns {{ valid: boolean, missing: string[] }}
 */
export function validateRsvpAnswers(answers) {
  return _validateAnswers(getRsvpQuestions(), answers ?? {});
}

/**
 * Expand a confirmed plus-one count into synthetic guest stubs the
 * seating planner can later promote to full guest records. Called
 * automatically by {@link submitRsvp} when `count > 1`.
 * @param {string} primaryGuestId
 * @param {number} plusOnes
 */
export function expandRsvpPlusOnes(primaryGuestId, plusOnes) {
  return _expandPlusOnes(primaryGuestId, plusOnes);
}

// ── S617: RSVP reminder scheduler wiring ─────────────────────────────────

/**
 * Build the default 3-wave reminder plan (30/14/3 days) for the current
 * event date and pending guests.
 *
 * @returns {ReturnType<typeof _planReminders>}
 */
export function getReminderPlan() {
  const info = /** @type {Record<string, string>} */ (storeGet("weddingInfo") ?? {});
  const guests = /** @type {any[]} */ (storeGet("guests") ?? []);
  return _planReminders(info.weddingDate ?? "", guests);
}

/**
 * Return the next reminder wave that is due as of today (or null if all
 * waves have passed or no event date is set).
 *
 * @param {string|Date} [today]
 * @returns {ReturnType<typeof _nextDueWave>}
 */
export function getNextDueReminder(today) {
  const info = /** @type {Record<string, string>} */ (storeGet("weddingInfo") ?? {});
  const guests = /** @type {any[]} */ (storeGet("guests") ?? []);
  return _nextDueWave(info.weddingDate ?? "", guests, today ?? new Date());
}
